<script lang="ts" setup>
import { OnClickOutside } from "@vueuse/components";

// components
import { Dropdown as VDropdown } from "floating-vue";
import { GButton } from "@gwrk/g-components";

// interfaces
import type { LinkWithSubLinks } from "@gwrk/g-components/interfaces";

// enums
import {
  ButtonTextAlignment,
  ButtonVariant,
  ButtonWidth,
  IconPlacement,
  Size,
} from "@gwrk/g-components/enums";
import { Icon } from "@/enums/Icon";
import { Route } from "@/enums/Route";
import { LegacyClientPanelUrl } from "@/enums/LegacyClientPanelUrl";

// composables
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

// stores
import { useUser } from "@/stores/useUser";
import { useCompanyId } from "@/stores/useCompanyId";

interface Props {
  links: LinkWithSubLinks[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (event: "close-sidebar"): void;
}>();

const { t } = useI18n();
const userStore = useUser();
const companyIdStore = useCompanyId();
const toast = useToast();

const logoSrc = `/${
  import.meta.env.VITE_APP_BASE_URL
}/images/svg/gowork-logo.svg`;

const logout = async () => {
  try {
    await userStore.logout();

    window.location.href = LegacyClientPanelUrl.PANEL;
  } catch (error) {
    toast.error(t("error.send_data_error"));
    console.error(error);
  }
};
</script>

<template>
  <OnClickOutside @trigger="emit('close-sidebar')">
    <div class="g-sidebar">
      <template v-for="link in props.links">
        <VDropdown
          v-if="link.subLinks.length && !link.disabled"
          :key="link.title"
          placement="auto-start"
          class="w-full"
        >
          <GButton
            class="g-sidebar__link"
            :button-variant="ButtonVariant.TRANSPARENT"
            :icon="Icon.CHEVRON_RIGHT_SOLID"
            :icon-size="Size.XS"
            :selected="link.selected"
            :width="ButtonWidth.FULL"
            :text-alignment="ButtonTextAlignment.LEFT"
          >
            <span class="g-sidebar__link--inner">{{ link.title }}</span>
          </GButton>
          <template #popper>
            <div class="flex flex-col items-center gap-2 max-w-xs">
              <template v-for="subLink in link.subLinks">
                <GButton
                  v-if="!subLink.disabled"
                  :key="subLink.title"
                  class="g-sidebar__link"
                  :href="subLink.href"
                  :button-variant="ButtonVariant.TRANSPARENT"
                  :selected="subLink.selected"
                  :width="ButtonWidth.FULL"
                  :text-alignment="ButtonTextAlignment.LEFT"
                >
                  <span class="g-sidebar__link--inner">{{
                    subLink.title
                  }}</span>
                </GButton>
              </template>
            </div>
          </template>
        </VDropdown>
        <GButton
          v-if="!link.subLinks.length && !link.disabled"
          :key="link.title"
          class="g-sidebar__link"
          :href="link.href"
          :button-variant="ButtonVariant.TRANSPARENT"
          :selected="link.selected"
          :width="ButtonWidth.FULL"
          :text-alignment="ButtonTextAlignment.LEFT"
        >
          <span class="g-sidebar__link--inner">{{ link.title }}</span>
        </GButton>
      </template>
      <VDropdown class="block w-full lg:hidden">
        <GButton
          :button-variant="ButtonVariant.TRANSPARENT"
          :icon-placement="IconPlacement.AFTER"
          :icon="Icon.CHEVRON_DOWN_SOLID"
          :icon-size="Size.SM"
        >
          {{ t("navigation.my_account") }}
        </GButton>
        <template #popper>
          <div class="flex flex-col items-center gap-2">
            <GButton
              :button-variant="ButtonVariant.TRANSPARENT"
              @click="logout"
            >
              {{ t("btn.logout") }}
            </GButton>
          </div>
        </template>
      </VDropdown>
      <GButton
        class="mr-auto absolute bottom-[2%] left-[8%] !w-1/2 lg:!hidden"
        :href="{
          name: Route.JOB_OFFERS_LISTING_ACTIVE,
          params: { companyId: companyIdStore.companyId },
        }"
        :button-variant="ButtonVariant.LINK"
        :padding="false"
      >
        <img class="g-navbar__logo" :alt="t('gowork.pl')" :src="logoSrc" />
      </GButton>
    </div>
  </OnClickOutside>
</template>

<style lang="scss" scoped>
.g-sidebar {
  border-right: 1px solid theme("colors.gray.300");
  overflow-x: auto;
  height: 100%;
  width: 16rem;

  @apply bg-white p-4 flex gap-4 flex-col items-center;

  &__link {
    @apply w-full justify-start;

    &--inner {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
